import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardMedia,
  Avatar,
  Divider,
  Skeleton,
} from "@mui/material";
import { NewsStore } from "../../store/newsStore";
import RightSidebar from "../../pages/sidebar/SideBar";
import Preloader from "../preloader/PreLoader";
import { BASE_URL } from "../../api/api";
import CustomPagination from "../custom-pagination/CustomPagination";
import dayjs from "dayjs";

const noImage = "https://upload.wikimedia.org/wikipedia/commons/d/d1/Image_not_available.png";

const LazyImage = ({ src, alt, sx, className, hideOnError }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    setLoaded(false);
    setError(false);
  }, [src]);

  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      if (imgRef.current.naturalWidth === 0) setError(true);
      else setLoaded(true);
    }
  }, [src]);

  if (error && hideOnError) return null;

  const displaySrc = error ? noImage : src;

  return (
    <Box sx={{ position: "relative", flexShrink: 0, ...sx }}>
      {!loaded && !error && (
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          animation="wave"
          sx={{ position: "absolute", top: 0, left: 0, zIndex: 1, borderRadius: sx?.borderRadius }}
        />
      )}
      <Box
        component="img"
        ref={imgRef}
        className={className}
        src={displaySrc}
        alt={alt}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        sx={{
          width: "100%",
          height: "100%",
          objectFit: displaySrc === noImage ? "contain" : (sx?.objectFit || "cover"),
          objectPosition: sx?.objectPosition || "center center",
          borderRadius: sx?.borderRadius,
          opacity: loaded || error ? 1 : 0,
          transition: "opacity 0.4s ease-in-out",
        }}
      />
    </Box>
  );
};

export default function ChhattisgarhPage() {
  const [searchParams] = useSearchParams();
  const tagQuery = searchParams.get("tag"); // Get tag from URL
  const { fetchNews, news, totalPages } = NewsStore();
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Helper to strip HTML tags for description
  const truncateHtmlText = (html, maxLength = 150) => {
    if (!html) return "";
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.textContent || div.innerText || '';
    return text.length > maxLength
      ? text.substring(0, maxLength) + '...'
      : text;
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [tagQuery]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // If a tag is present in the URL, we'll use it as a search query.
        // Otherwise, we'll fetch news for the 'chhattisgarh' category.
        // This assumes the backend search functionality includes searching by tags.
        const searchQuery = tagQuery || "";
        const categorySlug = tagQuery ? "" : "chhattisgarh";

        await fetchNews(currentPage, itemsPerPage, searchQuery, categorySlug, "");
      } catch (error) {
        console.error("Error fetching news:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [fetchNews, tagQuery, currentPage, itemsPerPage]);

  if (loading) return <Preloader />;

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", mt: { xs: 21, sm: 22, md: 23, lg: 22 }, pb: 10 }}>
      <Container maxWidth="xl">
        <Grid
          container
          spacing={4}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 300px" },
            gap: 4,
          }}
        >
          {/* Main Content */}
          <Box>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" fontWeight={700} sx={{ color: "#ff184e" }}>
                    {tagQuery ? `News Tagged: ${tagQuery.replace(/\s+/g, '-').toLowerCase()}` : "Chhattisgarh News"}
                </Typography>
                <Divider sx={{ mt: 1, borderColor: "#ff184e", width: "100px", borderBottomWidth: 3 }} />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {news && news.length > 0 ? (
                    news.map((item) => (
                        <Link
                            key={item.id}
                            to={`/${item.slug}`}
                            style={{ textDecoration: "none" }}
                        >
                            <Card
                                sx={{
                                    display: "flex",
                                    flexDirection: { xs: "column", sm: "row" },
                                    gap: 2,
                                    p: 2,
                                    borderRadius: 2,
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                    transition: "0.3s",
                                    "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.15)" },
                                }}
                            >
                                {item.image && (
                                    <LazyImage
                                        src={item.image ? (item.image.startsWith("http") ? item.image : `${BASE_URL.replace(/\/$/, "")}/${item.image.replace(/^\//, "")}`) : noImage}
                                        alt={item.title}
                                        hideOnError={true}
                                        sx={{
                                            width: { xs: "100%", sm: 250, md: 260 },
                                            height: { xs: 200, sm: 170, md: 180 },
                                            borderRadius: 2,
                                            objectPosition: "center center",
                                        }}
                                    />
                                )}
                                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: "red", fontWeight: 700, textTransform: "uppercase" }}>
                                            {item.category?.name}
                                        </Typography>
                                        <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3, mt: 1, color: "text.primary" }}>
                                            {item.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, display: { xs: "none", md: "block" } }}>
                                            {truncateHtmlText(item.description, item.image ? 120 : 300)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Avatar src={item.author?.profile_image || noImage} sx={{ width: 24, height: 24 }} />
                                            <Typography variant="caption" fontWeight={600} color="text.primary">
                                                {item.author?.name}
                                            </Typography>
                                        </Box>
                                        <Typography variant="caption" color="text.secondary">
                                            {dayjs(item.published_at).format("MMM D, YYYY")}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Card>
                        </Link>
                    ))
                ) : (
                    <Typography variant="h6" align="center" color="text.secondary" sx={{ mt: 4 }}>
                        {tagQuery ? `No news found for tag "${tagQuery}".` : "No news found in this category."}
                    </Typography>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CustomPagination
                      totalPages={totalPages}
                      currentPage={currentPage}
                      onPageChange={(page) => setCurrentPage(page)}
                    />
                  </Box>
                )}
            </Box>
          </Box>

          {/* Sidebar */}
          {/* <Box>
            <Box sx={{ position: "sticky", top: 100 }}>
                <RightSidebar />
            </Box>
          </Box> */}
        </Grid>
      </Container>
    </Box>
  );
}
